import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  getDocs,
  updateDoc,
  serverTimestamp,
  increment,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';
import { deleteUser } from 'firebase/auth';
import { hashPassword } from '../utils/cryptoUtils';
import { getCurrentDateTime, formatDateForFirebase } from '../utils/dateUtils';
import { logError, logInfo, logDebug, logWarn } from './logService';
import {
  getHabitsFromCache,
  setHabitsInCache,
  invalidateHabitsCache,
  getUserFromCache,
  setUserInCache,
  invalidateUserCache
} from './cacheService';

// Valores padrão para novos usuários
const DEFAULT_USER_DATA = {
  habits: [],
  achievements: {},
  achievementProgress: {},
  totalPerfectDays: 0,
  preferences: {
    mode: 'light',
    customColors: {
      primary: '#1B365D',
      secondary: '#78B6CB',
      highlightToday: true,
      highlightPerfectDay: true,
      habitSound: 'pop',
      allCompletedSound: 'achievement',
      showDailyQuote: true
    },
    initialTab: 'calendar'
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// Função auxiliar para atualizar última atividade
async function updateActivityIfEnabled(uid) {
  // Se quiser desabilitar todas as atualizações de atividade, basta retornar aqui
  // return;
  
  try {
    await updateLastActivity(uid);
  } catch (error) {
    // Ignora erros na atualização de atividade para não afetar a função principal
    console.warn('Erro ao atualizar última atividade (ignorando):', error);
  }
}

export async function createUserProfile(uid, email) {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      console.log('Criando novo perfil de usuário com dados padrão:', { uid, email });
      
      // Cria o documento do usuário com os valores padrão
      await setDoc(userRef, {
        ...DEFAULT_USER_DATA,
        email,
        uid
      });

      console.log('Perfil de usuário criado com sucesso');
      return true;
    }

    return false;
  } catch (error) {
    console.error('Erro ao criar perfil do usuário:', error);
    throw error;
  }
}

export async function getUserProfile(uid) {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Erro ao buscar perfil do usuário:', error);
    throw error;
  }
}

export async function getAllUsers() {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Erro ao buscar usuários:', error);
    throw error;
  }
}

export async function updateUserProfile(uid, updates) {
  try {
    const userRef = doc(db, 'users', uid);
    
    const updateData = {
      ...updates,
      updatedAt: getCurrentDateTime()
    };

    if (updates.password) {
      updateData.hashedPassword = hashPassword(updates.password);
      updateData.passwordLastChanged = getCurrentDateTime();
      delete updateData.password;
    }

    await updateDoc(userRef, updateData);

    // Atualiza última atividade
    await updateActivityIfEnabled(uid);

    return true;
  } catch (error) {
    console.error('Erro ao atualizar perfil do usuário:', error);
    throw error;
  }
}

export async function updateLastActivity(uid) {
  try {
    const userRef = doc(db, 'users', uid);
    const now = new Date().toISOString();
    
    console.log('Atualizando atividade do usuário:', {
      uid,
      now,
      timestamp: new Date()
    });

    // Atualiza lastActivity e incrementa o contador
    await updateDoc(userRef, {
      lastActivity: now,
      'metadata.activityCount': increment(1)
    });
    
    // Verifica se a atualização foi bem sucedida e monitora o contador
    const updatedDoc = await getDoc(userRef);
    const updatedData = updatedDoc.data();
    console.log('Dados após atualização:', {
      lastActivity: updatedData.lastActivity,
      activityCount: updatedData.metadata?.activityCount || 0
    });

    // Alerta se o número de atualizações estiver alto
    if (updatedData.metadata?.activityCount > 1000) {
      console.warn('Alto número de atualizações de atividade detectado:', {
        uid,
        count: updatedData.metadata.activityCount
      });
    }

    return true;
  } catch (error) {
    console.error('Erro ao atualizar última atividade:', error);
    throw error;
  }
}

// Nova função específica para atualizar login
export async function updateLastLogin(uid) {
  try {
    console.log('updateLastLogin foi chamada para', uid);
    const userRef = doc(db, 'users', uid);
    const now = new Date().toISOString();
    
    // Verifica se o documento existe
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      // Cria o documento básico se não existir
      await setDoc(userRef, {
        lastLogin: now,
        metadata: {
          lastLogin: now,
          loginCount: 1
        }
      }, { merge: true });
    } else {
      // Atualiza normalmente se já existir
    await updateDoc(userRef, {
      lastLogin: now,
      'metadata.lastLogin': now,
      'metadata.loginCount': increment(1)
    });
    }
    
    return true;
  } catch (error) {
    console.error('Erro ao atualizar último login:', error);
    throw error;
  }
}

export async function updateLogout(uid) {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      'metadata.lastLogout': getCurrentDateTime()
    });
    return true;
  } catch (error) {
    console.error('Erro ao atualizar logout:', error);
    throw error;
  }
}

export async function updatePasswordChanged(uid, newPassword) {
  try {
    const userRef = doc(db, 'users', uid);
    const now = getCurrentDateTime();
    
    await updateDoc(userRef, {
      hashedPassword: hashPassword(newPassword),
      passwordLastChanged: now,
      updatedAt: now
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao atualizar senha:', error);
    throw error;
  }
}

export async function updateUserHabits(uid, habits) {
  try {
    console.log('Iniciando atualização de hábitos:', {
      uid,
      habitsCount: habits.length
    });

    const userRef = doc(db, 'users', uid);
    
    // Atualiza os hábitos e espera a conclusão
    await updateDoc(userRef, {
      habits,
      updatedAt: getCurrentDateTime()
    });

    // Atualiza última atividade
    await updateActivityIfEnabled(uid);

    console.log('Hábitos atualizados com sucesso');
    
    // Busca os dados atualizados para confirmar
    const updatedDoc = await getDoc(userRef);
    const updatedData = updatedDoc.data();
    
    console.log('Dados após atualização:', {
      habitsCount: updatedData.habits?.length || 0,
      updatedAt: updatedData.updatedAt
    });

    return true;
  } catch (error) {
    console.error('Erro ao atualizar hábitos:', error);
    throw error;
  }
}

export async function updateUserPreferences(uid, preferences) {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      preferences,
      updatedAt: getCurrentDateTime()
    });

    // Atualiza última atividade
    await updateActivityIfEnabled(uid);

    return true;
  } catch (error) {
    console.error('Erro ao atualizar preferências:', error);
    throw error;
  }
}

export async function getUserData(uid) {
  try {
    console.log('Iniciando getUserData para uid:', uid);
    const userRef = doc(db, 'users', uid);
    console.log('Referência do documento criada:', userRef);
    
    const userSnap = await getDoc(userRef);
    console.log('Snapshot obtido:', userSnap);
    
    if (userSnap.exists()) {
      console.log('Documento existe, dados:', userSnap.data());
      const userData = userSnap.data();
      return {
        habits: userData.habits || DEFAULT_USER_DATA.habits,
        preferences: userData.preferences || DEFAULT_USER_DATA.preferences,
        achievements: userData.achievements || {}
      };
    }
    
    console.log('Documento não existe, retornando dados padrão');
    return {
      ...DEFAULT_USER_DATA,
      achievements: {}
    };
  } catch (error) {
    console.error('Erro ao buscar dados do usuário:', error);
    throw error;
  }
}

export async function deleteUserAccount(uid) {
  try {
    // Delete from Firestore
    const userRef = doc(db, 'users', uid);
    await deleteDoc(userRef);
    
    // Delete from Firebase Auth
    const user = auth.currentUser;
    if (user && user.uid === uid) {
      await deleteUser(user);
    }
    
    return true;
  } catch (error) {
    console.error('Erro ao excluir usuário:', error);
    throw error;
  }
}

export async function resetUserData(uid) {
  try {
    console.log('1. Iniciando reset de dados do usuário:', uid);
    
    // Tenta buscar os dados essenciais do usuário
    let userData = null;
    try {
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        userData = userDoc.data();
      }
    } catch (error) {
      console.warn('Erro ao buscar dados do usuário (usando dados padrão):', error);
    }

    // Prepara os dados resetados, mantendo informações essenciais
    const resetData = {
      ...DEFAULT_USER_DATA,
      email: userData?.email || '',
      uid: uid,
      role: userData?.role || 'user',
      hashedPassword: userData?.hashedPassword || '',
      metadata: {
        lastReset: getCurrentDateTime(),
        lastLogin: userData?.metadata?.lastLogin || null,
        lastLogout: userData?.metadata?.lastLogout || null,
        loginCount: userData?.metadata?.loginCount || 0,
        deviceInfo: userData?.metadata?.deviceInfo || {}
      },
      createdAt: userData?.createdAt || new Date().toISOString(),
      updatedAt: getCurrentDateTime()
    };

    // Tenta atualizar no Firestore de forma não-bloqueante
    const userRef = doc(db, 'users', uid);
    setDoc(userRef, resetData).catch(error => {
      console.warn('Erro ao salvar dados resetados no Firestore (ignorando):', error);
    });

    console.log('2. Reset de dados concluído localmente');
    return true;
  } catch (error) {
    console.error('Erro no processo de reset:', error);
    // Retorna true mesmo com erro para não bloquear a UI
    return true;
  }
}